import { useCallback, useEffect, useMemo, useState } from "react";

import {
  createTimelineStore,
  createTimelineStoreTransport,
  type TimelineStore,
  type VideoTimelineState,
} from "../Video";

interface PendingCapturedFrameSeek {
  readonly sequenceId: string;
  readonly streamId: string;
  readonly timestampSeconds: number;
}

function idleTimeline(durationSeconds: number): VideoTimelineState {
  return {
    playing: false,
    currentTimeSeconds: 0,
    durationSeconds,
    playbackRate: 1,
    status: "idle",
  };
}

/**
 * Studio 브라우즈 재생의 타임라인은 React 상태가 아니라 외부 store에 산다 — 재생 중
 * 시간 보고(10Hz)가 Studio 트리 전체를 리렌더하지 않도록, 시간 소비 지점만
 * `useVideoTimeline` 셀렉터로 국소 구독한다.
 */
export function useStudioPlayback({
  streamId,
  selectedSequenceId,
  selectedStreamId,
  durationSeconds,
}: {
  streamId: string | null | undefined;
  selectedSequenceId: string | null;
  selectedStreamId: string | null;
  durationSeconds?: number | null;
}) {
  const [store] = useState<TimelineStore>(() => createTimelineStore(idleTimeline(0)));
  const transport = useMemo(() => createTimelineStoreTransport(store), [store]);
  const [pendingCapturedFrameSeek, setPendingCapturedFrameSeek] =
    useState<PendingCapturedFrameSeek | null>(null);

  useEffect(() => {
    store.setSnapshot(idleTimeline(durationSeconds ?? 0));
  }, [durationSeconds, selectedSequenceId, streamId, store]);

  useEffect(() => {
    if (
      !pendingCapturedFrameSeek ||
      pendingCapturedFrameSeek.sequenceId !== selectedSequenceId ||
      pendingCapturedFrameSeek.streamId !== selectedStreamId
    ) {
      return;
    }

    const current = store.getSnapshot();
    const resolvedDuration = durationSeconds ?? current.durationSeconds;
    const upperBound =
      Number.isFinite(resolvedDuration) && resolvedDuration > 0
        ? resolvedDuration
        : pendingCapturedFrameSeek.timestampSeconds;
    store.setSnapshot({
      playing: false,
      currentTimeSeconds: Math.min(
        Math.max(pendingCapturedFrameSeek.timestampSeconds, 0),
        upperBound,
      ),
    });
    setPendingCapturedFrameSeek(null);
  }, [durationSeconds, pendingCapturedFrameSeek, selectedSequenceId, selectedStreamId, store]);

  const cancelCapturedFrameSeek = useCallback(() => setPendingCapturedFrameSeek(null), []);

  return {
    store,
    transport,
    requestCapturedFrameSeek: setPendingCapturedFrameSeek,
    cancelCapturedFrameSeek,
  };
}
